import { useState, useEffect, useMemo } from 'react';
import { Ambassador, AmbassadorData } from '../types';
import { enrichAmbassadorWithCoordinates } from '../utils/geocoding';

interface UseAmbassadorsReturn {
  ambassadors: Ambassador[];
  loading: boolean;
  error: string | null;
  metadata: AmbassadorData['metadata'] | null;
  refetch: () => void;
}

export function useAmbassadors(): UseAmbassadorsReturn {
  const [ambassadors, setAmbassadors] = useState<Ambassador[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<AmbassadorData['metadata'] | null>(null);

  const loadAmbassadors = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/data/ambassadors.json');
      if (!response.ok) {
        throw new Error(`Failed to load ambassadors: ${response.status}`);
      }

      const data: AmbassadorData = await response.json();
      
      // Enrich ambassadors with coordinates and pre-compute search blobs
      const enrichedAmbassadors = data.ambassadors.map((ambassador) => {
        const enriched = enrichAmbassadorWithCoordinates(ambassador);
        // Pre-compute search blob for performance
        const searchBlob = [
          enriched.fullName,
          enriched.city,
          enriched.country,
          enriched.businessUnit,
          enriched.activity,
          enriched.role,
          enriched.region,
        ].join(' ').toLowerCase();
        
        return {
          ...enriched,
          searchBlob,
        };
      });
      
      setAmbassadors(enrichedAmbassadors);
      setMetadata(data.metadata);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load ambassadors';
      setError(errorMessage);
      console.error('Error loading ambassadors:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAmbassadors();
  }, []);

  return {
    ambassadors,
    loading,
    error,
    metadata,
    refetch: loadAmbassadors,
  };
}

// Helper hook for statistics
export function useAmbassadorStats(ambassadors: Ambassador[]) {
  return useMemo(() => {
    const stats = {
      total: ambassadors.length,
      byRole: {} as Record<string, number>,
      byCountry: {} as Record<string, number>,
      byBusinessUnit: {} as Record<string, number>,
      byActivity: {} as Record<string, number>,
      byRegion: {} as Record<string, number>,
    };

    ambassadors.forEach((ambassador) => {
      // Count by role
      stats.byRole[ambassador.role] = (stats.byRole[ambassador.role] || 0) + 1;
      
      // Count by country
      stats.byCountry[ambassador.country] = (stats.byCountry[ambassador.country] || 0) + 1;
      
      // Count by business unit
      stats.byBusinessUnit[ambassador.businessUnit] = (stats.byBusinessUnit[ambassador.businessUnit] || 0) + 1;
      
      // Count by activity
      stats.byActivity[ambassador.activity] = (stats.byActivity[ambassador.activity] || 0) + 1;
      
      // Count by region
      stats.byRegion[ambassador.region] = (stats.byRegion[ambassador.region] || 0) + 1;
    });

    return stats;
  }, [ambassadors]);
}

// Helper hook for geographic data
export function useGeographicData(ambassadors: Ambassador[]) {
  return useMemo(() => {
    const validCoordinates = ambassadors.filter(
      (ambassador) => ambassador.latitude && ambassador.longitude
    );

    if (validCoordinates.length === 0) {
      return {
        bounds: {
          north: 70.0,
          south: 35.0,
          east: 30.0,
          west: -10.0,
        },
        coordinates: [],
        center: { latitude: 50.0755, longitude: 4.4357 },
      };
    }

    const lats = validCoordinates.map((a) => a.latitude!);
    const lngs = validCoordinates.map((a) => a.longitude!);

    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };

    // Add padding to bounds
    const latPadding = (bounds.north - bounds.south) * 0.1;
    const lngPadding = (bounds.east - bounds.west) * 0.1;

    return {
      bounds: {
        north: bounds.north + latPadding,
        south: bounds.south - latPadding,
        east: bounds.east + lngPadding,
        west: bounds.west - lngPadding,
      },
      coordinates: validCoordinates.map((ambassador) => ({
        latitude: ambassador.latitude!,
        longitude: ambassador.longitude!,
        ambassador,
      })),
      center: {
        latitude: (bounds.north + bounds.south) / 2,
        longitude: (bounds.east + bounds.west) / 2,
      },
    };
  }, [ambassadors]);
}